import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { GoogleMap, Marker,Polyline } from "@capacitor/google-maps";
import { NavController } from '@ionic/angular';
import { googleMapsApiKey } from '../../../../../../../../common/runtime-config';
@Component({
  selector: 'app-product-tracking-map',
  templateUrl: './product-tracking-map.component.html',
  styleUrls: ['./product-tracking-map.component.scss'],
})
export class ProductTrackingMapComponent implements OnInit {
  @Input("steps") steps: any;
  @Input("workflowId") workflowId: any;
  @Input("ean") ean: any;
  @Input("batchId") batchId: any;
  map: GoogleMap;
  @ViewChild("trackingMap") trackingMap: ElementRef<HTMLElement>
  mapZoom:number = 12;
  markers:Array<any> = [];
  constructor(private navController: NavController) { }
  polyline:  Polyline;
  polylineOptions: any = {strokeColor: '#000000', strokeOpacity: 1, strokeWeight: 5};
  ngOnInit() {
    // `steps` is optional: a product whose lot has nothing published, or one whose input has
    // not arrived yet when this timer fires, used to reach the loop below with `undefined` and
    // throw inside an async nobody awaits — an unhandled rejection in the browser of whoever
    // scanned the QR. With no step there is no map to build, so it returns.
    setTimeout(() => {
      if (!this.steps || this.steps.length === 0) return
      this.initTrackingMap()
    }, 500)
  }

  async initTrackingMap(){    
    // Collect all activities from all steps
    const allActivities: any[] = [];
    
    this.steps.forEach((step: any) => {
      const activities = step.activities || [];
      activities.forEach((activity: any) => {
        if (activity && activity.facility) {
          allActivities.push(activity);
        }
      });
    });
    
    // Build connections based on batch relationships: batchOut -> batchIn
    // Create a map: batchOut.id -> activity that produces it
    const batchOutMap = new Map<string, any>();
    const batchInMap = new Map<string, any[]>();
    
    allActivities.forEach((activity: any) => {
      // Map batchOut IDs to the activity that produces them
      const batchOuts = activity.batchOut || [];
      batchOuts.forEach((batchOut: any) => {
        if (batchOut && batchOut.id) {
          batchOutMap.set(batchOut.id.toString(), activity);
        }
      });
      
      // Map batchIn IDs to activities that consume them
      const batchIns = activity.batchIn || [];
      batchIns.forEach((batchIn: any) => {
        if (batchIn && batchIn.id) {
          if (!batchInMap.has(batchIn.id.toString())) {
            batchInMap.set(batchIn.id.toString(), []);
          }
          batchInMap.get(batchIn.id.toString())!.push(activity);
        }
      });
    });
    
    // Build routes based on batch relationships: batchOut -> batchIn
    // A route is an ordered list of activities connected via batches
    const routes: any[][] = [];
    const visitedInRoute = new Set<string>(); // for cycle protection per global traversal

    // Find starting activities (those with batchIn that don't come from other activities, or no batchIn)
    let startingActivities = allActivities.filter((activity: any) => {
      const batchIns = activity.batchIn || [];
      if (batchIns.length === 0) return true;

      // Check if any batchIn comes from another activity in our set
      return !batchIns.some(
        (bi: any) => bi && bi.id && batchOutMap.has(bi.id.toString())
      );
    });

    // Fallback: if no clear starting activities (e.g. cyclic data), use all activities sorted by date
    if (startingActivities.length === 0) {
      startingActivities = [...allActivities].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateA - dateB;
      });
    } else {
      // Sort starting activities by createdAt
      startingActivities.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateA - dateB;
      });
    }

    // Depth-first traversal to build all possible routes
    const dfsBuildRoute = (
      activity: any,
      currentRoute: any[],
      pathVisited: Set<string>
    ) => {
      const activityKey = `${activity.facility?.id}_${activity.createdAt}`;
      if (pathVisited.has(activityKey)) {
        return; // avoid cycles in a single route
      }

      const newRoute = [...currentRoute, activity];
      const newVisited = new Set(pathVisited);
      newVisited.add(activityKey);
      visitedInRoute.add(activityKey);

      // Find next activities: those whose batchIn matches this activity's batchOut
      const batchOuts = activity.batchOut || [];
      const nextActivities: any[] = [];

      batchOuts.forEach((batchOut: any) => {
        if (batchOut && batchOut.id) {
          const consumers = batchInMap.get(batchOut.id.toString()) || [];
          consumers.forEach((consumer: any) => {
            const consumerKey = `${consumer.facility?.id}_${consumer.createdAt}`;
            if (!newVisited.has(consumerKey)) {
              nextActivities.push(consumer);
            }
          });
        }
      });

      if (nextActivities.length === 0) {
        // End of route
        routes.push(newRoute);
        return;
      }

      // Sort next activities by createdAt and continue building the route
      nextActivities.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateA - dateB;
      });

      nextActivities.forEach((nextActivity: any) => {
        dfsBuildRoute(nextActivity, newRoute, newVisited);
      });
    };

    // Start DFS from each starting activity
    startingActivities.forEach((activity: any) => {
      dfsBuildRoute(activity, [], new Set<string>());
    });

    // Fallback: if for some reason no routes were built, create a single chronological route
    if (routes.length === 0 && allActivities.length > 0) {
      const sorted = [...allActivities].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateA - dateB;
      });
      routes.push(sorted);
    }

    // Create markers: one per facility (deduplicated globally)
    const facilityMarkerMap = new Map<string, any>();
    routes.forEach((route) => {
      route.forEach((activity: any) => {
        const facilityId = activity.facility?.id;
        if (facilityId && !facilityMarkerMap.has(facilityId)) {
          facilityMarkerMap.set(facilityId, activity);
        }
      });
    });

    facilityMarkerMap.forEach((activity: any) => {
      const marker = this.getMarker(activity);
      this.markers.push(marker);
    });
    // Creating the real map can reject for reasons entirely outside our control - a
    // visitor with the Google Maps script blocked, no network, or an expired/quota-exceeded
    // API key. That is a real, expected outcome for the public QR page, not a bug, so it must
    // be caught and logged instead of becoming an unhandled promise rejection that would take
    // the rest of the page down with it.
    try {
      this.map = await GoogleMap.create({
        id: "tracking-map",
        element: this.trackingMap.nativeElement,
        apiKey: googleMapsApiKey(),
        config: {
          center: this.getCenterByMarkers(),
          zoom: this.mapZoom,
          disableDefaultUI: true, 
          // Array vacío para mostrar el mapa a color
          styles: [],
        },
      });

      this.markers.map((marker:any)=>{
        this.map.addMarker(marker)
      })

      this.addPolyLines(routes)
    } catch (error) {
      console.error("ProductTrackingMapComponent: no se pudo inicializar el mapa", error)
    }
  }

  addPolyLines(routes: any[][]){
    if (!this.map) {
      return;
    }

    const colors = ['#000000', '#007bff', '#28a745', '#ffc107', '#dc3545'];
    const lines: Polyline[] = [];

    if (routes && routes.length > 0) {
      routes.forEach((route, index) => {
        const path = route
          .filter((activity: any) => activity && activity.facility)
          .map((activity: any) => {
            const lati = parseFloat(activity.facility.latitude);
            const longi = parseFloat(activity.facility.longitude);
            return { lat: lati, lng: longi };
          });

        // Need at least two points to see a line
        if (path.length >= 2) {
          lines.push({
            path,
            ...this.polylineOptions,
            strokeColor: colors[index % colors.length],
          });
        }
      });
    }

    // Fallback: if no valid route-based paths, draw a single line using markers (old behavior)
    if (lines.length === 0 && this.markers && this.markers.length >= 2) {
      const fallbackPath = this.markers.map((coord: any) => {
        return { lat: coord.coordinate.lat, lng: coord.coordinate.lng };
      });

      lines.push({
        path: fallbackPath,
        ...this.polylineOptions,
      });
    }

    if (lines.length > 0) {
      this.map.addPolylines(lines);
    }
  }
  getMarker(step): Marker {
    const lati = parseFloat(step.facility.latitude);
    const longi = parseFloat(step.facility.longitude);

    return {
        coordinate: {
            lat: lati,
            lng: longi
        },
        iconUrl:'/assets/img/portesa/map-marker3.png',
        iconAnchor: { x: 20, y: 35 },
        iconOrigin: { x: 0, y: 0 },
        iconSize: {
          width: 40,
          height: 40,
        },
        title: step.facility.name,
        snippet: step.facility.address
    }
}
 getCenterByMarkers() {

        const minLat = Math.min(...this.markers.map((coord: any) => coord.coordinate.lat));
        const maxLat = Math.max(...this.markers.map((coord: any) => coord.coordinate.lat));
        const minLng = Math.min(...this.markers.map((coord: any) => coord.coordinate.lng));
        const maxLng = Math.max(...this.markers.map((coord: any) => coord.coordinate.lng));

        const centerLat = (minLat + maxLat) / 2;
        const centerLng = (minLng + maxLng) / 2;

        const latDistance = Math.abs(maxLat - minLat);
        const lngDistance = Math.abs(maxLng - minLng);

        const zoomLevel = this.calculateZoomLevel(latDistance, lngDistance);
        const center = {lat: centerLat, lng: centerLng};
        this.mapZoom = zoomLevel;
        return center;
    }

    calculateZoomLevel(latDistance: number, lngDistance: number): number {
      
        const maxDistance = Math.max(latDistance, lngDistance);
        const worldWidth = 256; 
        const zoom = Math.floor(Math.log2(worldWidth / maxDistance));
        return zoom;
    }


    showTrackingMapPage(){
      //this.navController.navigateForward("/tracking-map/" + this.workflowId)
    }
}
